// map of pathwright domains to environment
export const PATHWRIGHT_DOMAIN_RES = new Map([
  [/\.(pathwright)\.com/, "production"],
  [/\.(pathwrightstaging)\.com/, "staging"],
  [/\.(pathwrightdev)\.com/, "development"],
  [/\.(pantswright)\.com/, "testing"]
])

type Environment = "production" | "staging" | "development" | "testing"
type EnvironmentMatcher = Partial<Record<Environment | "_", any>>
type DomainMatch = { match: RegExpMatchArray; env: Environment } | null

declare const process: {
  env: {
    NODE_ENV?: string
    REACT_APP_FEATURES_SANDBOX?: string
    REACT_APP_GQL?: string
    REACT_APP_GQL_ENV?: string
  }
}

const ENVIRONMENTS: Environment[] = [
  "production",
  "staging",
  "development",
  "testing"
]

// For typing
export const getNodeEnvValue = (): string | undefined => {
  return process.env.NODE_ENV
}

// find the matching pathwright domain re entry, replacing the re with the match
export const getPathwrightDomainMatch = (url?: string): DomainMatch => {
  const hostname = url || window.location.hostname
  return [...PATHWRIGHT_DOMAIN_RES.entries()].reduce<DomainMatch>(
    (result, [re, env]) =>
      result
        ? result
        : re.test(hostname)
        ? { match: hostname.match(re)!, env: env as Environment }
        : null,
    null
  )
}

// get the environment that matches the window.location.hostname
export const getDomainEnv = (url?: string): Environment | undefined => {
  return getPathwrightDomainMatch(url)?.env
}

// get the pathwright domain from the window.location.hostname
export const getPathwrightDomain = (url?: string): string | undefined => {
  return getPathwrightDomainMatch(url)?.match[1]
}

export type TargetEnv = Environment | Environment[] | EnvironmentMatcher

export const getEnv = (targetEnv?: TargetEnv): Environment => {
  // get environment based on domain
  let env = getDomainEnv()

  switch (getNodeEnvValue()) {
    // only allow production environment when set via NODE_ENV
    case "production":
      // and when not overridden by domain
      if (!env) env = "production"
      break
    case "test":
      // Ensure test env is not overridden.
      env = "test" as Environment
      break
  }

  // default to development environment
  if (!env) env = "development"

  if (targetEnv) {
    return getTargetEnv(env, targetEnv!)
  }

  return env
}

export function getTargetEnv(
  env: Environment,
  targetEnv: TargetEnv
): boolean | any {
  // targetEnv may include env as item in array
  if (Array.isArray(targetEnv)) return targetEnv.includes(env)
  // targetEnv may include env as key in object, or default to _ key
  if (typeof targetEnv === "object" && targetEnv !== null)
    return Object.prototype.hasOwnProperty.call(targetEnv, env)
      ? (targetEnv as EnvironmentMatcher)[env]
      : (targetEnv as EnvironmentMatcher)._
  // targetEnv may match env as string
  if (typeof targetEnv === "string") return env === targetEnv

  return env
}

// The environment of the GraphQL backend the client was started against, e.g.
// via `yarn dev:staging`. Undefined when the client was started without one
// (`yarn start`), in which case requests go to whatever runs on localhost:8000.
export function getConfiguredGraphQLEnv(): Environment | undefined {
  const host = process.env.REACT_APP_GQL
  const hostEnv = host ? getDomainEnv(host) : undefined
  if (hostEnv) return hostEnv

  const env = process.env.REACT_APP_GQL_ENV as Environment | undefined
  return env && ENVIRONMENTS.includes(env) ? env : undefined
}

const PATHWRIGHT_DOMAINS: Record<Environment, string> = {
  production: "pathwright",
  staging: "pathwrightstaging",
  development: "pathwrightdev",
  testing: "pantswright"
}

export type SchoolEnvMismatch = {
  schoolEnv: Environment
  graphqlEnv: Environment
  // The same school host on the GraphQL backend's domain. Undefined for custom
  // domains, which have no host in another environment.
  matchingSchoolUrl?: string
}

// Compares a local dev school URL with the configured GraphQL backend. A custom
// domain belongs to a production school.
export function getSchoolEnvMismatch(
  schoolUrl: string
): SchoolEnvMismatch | undefined {
  const graphqlEnv = getConfiguredGraphQLEnv()
  if (!graphqlEnv) return

  const domainMatch = getPathwrightDomainMatch(schoolUrl)
  const schoolEnv = domainMatch?.env || "production"
  if (schoolEnv === graphqlEnv) return

  const matchingSchoolUrl = domainMatch
    ? schoolUrl
        .replace(/^https?:\/\//, "")
        .replace(
          `.${domainMatch.match[1]}.com`,
          `.${PATHWRIGHT_DOMAINS[graphqlEnv]}.com`
        )
    : undefined

  return { schoolEnv, graphqlEnv, matchingSchoolUrl }
}

// Get the env in which the application is currently running, based on the domain.
// This is primarily useful for determining the correct target env when the app
// is running in a local dev environment, where the domain will not indicate the
// intended target env.
export function getOperatingEnv() {
  // A deployed Features sandbox has a Vercel hostname, not a school hostname.
  // Only its entrypoint defines this flag; normal app/custom-domain detection
  // stays unchanged. Never infer production for a testing/staging sandbox.
  if (process.env.REACT_APP_FEATURES_SANDBOX === "1") {
    const sandboxEnv = process.env.REACT_APP_GQL_ENV
    if (sandboxEnv === "testing" || sandboxEnv === "staging") return sandboxEnv
  }
  if (getIsLocalHost()) {
    // A client pinned to a deployed GraphQL backend must target that backend's
    // environment, whatever school a previous session left in localStorage.
    const configuredEnv = getConfiguredGraphQLEnv()
    if (configuredEnv) return configuredEnv
  }

  // "school_config_url" should only exist in local dev.
  const hostname = getIsLocalHost()
    ? localStorage.getItem("school_config_url")
    : window.location.hostname

  let env = getDomainEnv(hostname!)

  // If we can't determine env from hostname, we must be in production...
  if (!env && hostname) {
    // ... either the deployed production env with a custom domain
    if (getNodeEnvValue() === "production") {
      env = "production"
    }

    // ... or a local dev env using a production API
    if (getIsLocalHost()) {
      env = "production"
    }
  }

  if (!env) {
    throw new Error("Could not determine admin environment.")
  }

  return env
}

export const getIsLocalHost = (): boolean =>
  !getDomainEnv() && getEnv() !== "production"
export const getIsLocalDev = (): boolean =>
  getIsLocalHost() || getEnv() === "development"
